import { supabase } from './supabaseClient'
import { isNativeApp } from './platform'

const REGISTRY_LANGUAGES = new Set(['fr','de','it','en'])
export const registryLanguage = lang => REGISTRY_LANGUAGES.has(lang) ? lang : 'fr'
const DATE_LOCALES = {fr:'fr-CH',de:'de-CH',it:'it-CH',en:'en-CH'}
const ERRORS = {
  fr:{auth_changed:'Votre session a changé. Rechargez cette page.',timeout:'La liste met trop de temps à répondre. Réessayez.',unavailable:'Les nouvelles inscriptions sont temporairement indisponibles.'},
  de:{auth_changed:'Ihre Sitzung hat sich geändert. Laden Sie diese Seite neu.',timeout:'Die Liste braucht zu lange zum Antworten. Versuchen Sie es erneut.',unavailable:'Die neuen Eintragungen sind vorübergehend nicht verfügbar.'},
  it:{auth_changed:'La sessione è cambiata. Ricarichi questa pagina.',timeout:'La lista impiega troppo tempo a rispondere. Riprovi.',unavailable:'Le nuove iscrizioni sono temporaneamente indisponibili.'},
  en:{auth_changed:'Your session has changed. Reload this page.',timeout:'The list is taking too long to respond. Try again.',unavailable:'New registrations are temporarily unavailable.'},
}
const failure = code => Object.assign(new Error(code), {code})
const clean = (value, limit = 300) => typeof value === 'string' ? value.trim().slice(0,limit) : ''
export const registryFeedEnabled = (hostname = window.location.hostname, native = isNativeApp()) => !native && ['app.hubbing.ch','localhost','127.0.0.1','[::1]'].includes(String(hostname).toLowerCase())
export const registryFeedError = (error,lang = 'fr') => {
  const messages = ERRORS[registryLanguage(lang)],code = typeof error === 'string' ? error : error?.code
  return ['auth_changed','timeout'].includes(code) ? messages[code] : messages.unavailable
}
export const registryDate = (value,lang = 'fr') => {
  const date = new Date(value)
  return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat(DATE_LOCALES[registryLanguage(lang)],{day:'2-digit',month:'2-digit',timeZone:'Europe/Zurich'}).format(date) : '—'
}
export function registryZefixNewCompaniesUrl(now = new Date(),lang = 'fr') {
  const language = registryLanguage(lang)
  try {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA',{timeZone:'Europe/Zurich',year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(now).map(part => [part.type,part.value]))
    const end = `${parts.year}-${parts.month}-${parts.day}`
    const start = new Date(Date.UTC(Number(parts.year),Number(parts.month)-1,Number(parts.day)-6)).toISOString().slice(0,10)
    return `https://www.zefix.admin.ch/${language}/search/shab/list?${new URLSearchParams({mutationTypes:'2',publicationDate:start,publicationDateEnd:end,searchTypeExact:'true'})}`
  } catch { return `https://www.zefix.admin.ch/${language}/search/shab/welcome` }
}
function sourceUrl(value) {
  try {
    const url = new URL(value)
    if (url.protocol === 'https:' && ['amtsblattportal.ch','www.amtsblattportal.ch','www.shab.ch','shab.ch','www.zefix.admin.ch','zefix.admin.ch'].includes(url.hostname) && !url.username && !url.password) return url.href
  } catch {}
  return null
}
export function parseRegistryFeed(data) {
  if (!Array.isArray(data?.companies)) throw failure('invalid_response')
  const unique = new Map()
  data.companies.forEach(row => {
    const uid = clean(row?.uid,40).replace(/[^a-z0-9]/gi,'').toUpperCase()
    if (!/^CHE\d{9}$/.test(uid)) throw failure('invalid_response')
    if (!unique.has(uid)) unique.set(uid,{
      uid, name:clean(row?.name), city:clean(row?.city,120), canton:clean(row?.canton,2),
      publishedAt:clean(row?.publishedAt,50), publicationId:clean(row?.publicationId,80), sourceUrl:sourceUrl(row?.sourceUrl),
    })
  })
  return {companies:[...unique.values()].slice(0,20),refreshedAt:clean(data.refreshedAt,50),stale:data.stale === true}
}

// Auth is pinned to the current Home identity; this operation only reads the
// informative feed. No invitation, contact, or email action is exposed here.
export async function readRegistryFeed({expectedUserId,signal,client = supabase} = {}) {
  if (!expectedUserId) throw failure('auth_changed')
  const controller = new AbortController()
  let timer,onAbort
  const deadline = new Promise((_,reject) => {
    onAbort = () => {controller.abort();reject(failure('aborted'))}
    signal?.addEventListener('abort',onAbort,{once:true})
    if (signal?.aborted) onAbort()
    timer = window.setTimeout(() => {controller.abort();reject(failure('timeout'))},12000)
  })
  const work = Promise.resolve().then(async () => {
    if (controller.signal.aborted) throw failure('aborted')
    const {data,error} = await client.auth.getSession()
    const session = data?.session
    if (error || session?.user?.id !== expectedUserId || !session?.access_token) throw failure('auth_changed')
    if (controller.signal.aborted) throw failure('aborted')
    const result = await client.functions.invoke('registry-invitations',{
      body:{action:'feed'}, headers:{Authorization:`Bearer ${session.access_token}`}, signal:controller.signal,
    })
    if (controller.signal.aborted) throw failure('aborted')
    if (result.error || result.data?.ok !== true) throw failure('unavailable')
    return parseRegistryFeed(result.data)
  })
  try {return await Promise.race([work,deadline])}
  finally {window.clearTimeout(timer);signal?.removeEventListener('abort',onAbort)}
}
